import { camelCase } from 'lodash'
import { urlAddExist, urlRemove } from 'Utils/URL'
import { getStartEndDateFromWeekFilter } from '../../../../Utils/Table'
import {
  browserSafeNewDate,
  dropTimeZoneFromDate,
  get24HourTime,
  getShortWeekDay,
  getWeekNumber,
  toLocaleNonAmericanDateString,
} from '../../../../shared/libraries/utils/date'
import { escapeCsvString } from 'shared/csv'
import { downloadStringAsFile } from 'Utils/Misc'
import { exportHeaders } from './options'
import { MISSION_STATUS_ENUM } from '../../../../shared/constants'

const setUrl = (label, value) => {
  if (value && value !== 'All') {
    urlAddExist(label, value)
  } else {
    urlRemove(label)
  }
}

const calculateTimeOfDay = mission => {
  var localDateTimeStr = dropTimeZoneFromDate(mission.dateTime)
  var localTime = localDateTimeStr.slice('yyyy-mm-ddT'.length)
  if (localTime < mission.cityPlan.city.lunchCheckInTime) return 'MORNING'
  if (localTime < mission.cityPlan.city.afternoonCheckInTime) return 'LUNCH'
  return 'AFTERNOON'
}

const getMetrics = node => {
  const getMetric = value => {
    if (!value) {
      return ' - '
    }
    const currentMetric = node.metricResults.nodes.find(
      r => r.metric.id === value.id,
    )
    return `${value.label ? value.label : value.name}: ${
      currentMetric ? currentMetric.value : 0
    }`
  }
  const metric1 = getMetric(node.cityPlan.campaign.metrics.nodes[0])
  const metric2 = getMetric(node.cityPlan.campaign.metrics.nodes[1])
  return [metric1, metric2]
}

const getStatus = node => {
  if (node.cancelled) {
    if (node.payoutRatio == 0) {
      return MISSION_STATUS_ENUM.CANCELLED
    }
    if (node.status === MISSION_STATUS_ENUM.PAID) {
      return 'CANCELLED_PAID'
    }
  }
  return node.status
}

const formatRows = (data, isCountryLevel) => {
  const rowsData = isCountryLevel ? data.country.missions : data.city.missions
  const weekAlwaysTwoDigits = week =>
    Number(week) > 0 && Number(week) < 10 ? `0${week}` : week

  return {
    totalCount: rowsData.totalCount,
    nodes: rowsData.edges.map(({ node }) => {
      const localDateStr = dropTimeZoneFromDate(node.dateTime)
      const localDate = browserSafeNewDate(localDateStr)

      const metrics = getMetrics(node)

      const formattedStatus = getStatus(node)
      return {
        id: node.id,
        clientName: node.cityPlan.campaign.client.name,
        cityName: isCountryLevel ? node.cityName : '',
        campaignName: node.campaign.name,
        week: 'W' + weekAlwaysTwoDigits(getWeekNumber(localDate)),
        date: toLocaleNonAmericanDateString(localDate),
        time: get24HourTime(localDate),
        timeOfDay: calculateTimeOfDay(node),
        dayOfWeek: getShortWeekDay(localDate.getDay()),
        canBeSpied: node.canBeSpied,
        location: node.location ? node.location.name : ' - ',
        brandAmbassador: node.user
          ? node.user.firstName + ' ' + node.user.lastName
          : '',
        baId: node.user ? node.user.id : null,
        cityId: isCountryLevel ? node.cityPlan.city.id : data.city.id,
        flyerCode: node.flyerCodeV2 ? node.flyerCodeV2.code : ' - ',
        tag: node.flyerCodeV2 ? node.flyerCodeV2.tag : ' - ',
        flyersDistributed: node.totalFlyersDistributed,
        scans: node.amountScans,
        metric1: metrics[0] ? metrics[0] : ' - ',
        metric2: metrics[1] ? metrics[1] : ' - ',
        status: formattedStatus,
        ratingInfo: node.ratingInfo,
        spy:
          node.spyInformation && node.spyInformation.spy
            ? `${node.spyInformation.spy.firstName} ${
                node.spyInformation.spy.lastName
              }`
            : false,
        isReported:
          node.spyInformation &&
          node.spyInformation.spyDetails &&
          node.spyInformation.spyDetails.reportReason,
        isSpied:
          node.spyInformation &&
          node.spyInformation.spyDetails &&
          node.spyInformation.spyDetails.checkInTime,
        shiftId:
          node.spyInformation &&
          node.spyInformation.shift &&
          node.spyInformation.shift.id,
        isNotShown: node.isNotShown,
        isStandby: !!node.isStandby,
        teamLeaderGroupId: node.teamLeaderGroupId,
        locationId: node.location ? node.location.id : null,
        type: node.type,
      }
    }),
  }
}

const switchOrderBy = value => {
  switch (value) {
    case 'date':
      return 'dateTime'
    case 'week':
      return 'dateTime'
    case 'time':
      return 'dateTime'
    case 'brandAmbassador':
      return 'user'
    default:
      return value
  }
}

const switchLabelToId = value => {
  switch (value) {
    case 'City':
      return 'cityIds'
    case 'Client':
      return 'clientIds'
    case 'Week':
      return 'week'
    case 'Location':
      return 'locationIds'
    case 'Campaign':
      return 'campaignIds'
    case 'Status':
      return 'status'
    case 'Time of day':
      return 'timeOfDays'
    case 'Day of week':
      return 'dayOfTheWeeks'
    case 'Reported':
      return 'reporteds'
    case 'Month':
      return 'includeMonths'
    case 'Year':
      return 'includeYears'
    default:
      return null
  }
}

const checkValue = value => (value === 'All' ? null : value)

const newValues = (prevValues, newValue, isAdd) => {
  let result = []
  if (isAdd) {
    if (prevValues) {
      result = prevValues.includes(newValue)
        ? [...prevValues]
        : [...prevValues, newValue]
    } else {
      result = [newValue]
    }
  } else {
    if (prevValues) {
      result = prevValues.filter(value => value != newValue)
    } else {
      result = null
    }
  }
  if (result && result.length == 0) result = null
  return result
}

const getUpdatedQueryAndLabels = (prev, arrValues, isAdd = true) => {
  const newVal = prev

  arrValues.forEach(value => {
    const { label, labelValue, queryValue } = value
    const newLabel = switchLabelToId(label)
    if (queryValue == 'All') {
      newVal.labels = { ...newVal.labels, [label]: null }
      if (newLabel === 'week') {
        newVal.query = {
          ...newVal.query,
          startDates: null,
          endDates: null,
        }
      } else if (newLabel === 'status') {
        newVal.query = {
          ...newVal.query,
          statusAndBelows: null,
          statusAndAboves: null,
        }
      } else {
        newVal.query = {
          ...newVal.query,
          [newLabel]: null,
        }
      }
    } else {
      newVal.labels = {
        ...newVal.labels,
        [label]: newValues(newVal.labels[label], labelValue, isAdd),
      }
      if (newLabel === 'week') {
        const { startDate, endDate } = getStartEndDateFromWeekFilter(queryValue)
        newVal.query = {
          ...newVal.query,
          startDates: newValues(newVal.query['startDates'], startDate, isAdd),
          endDates: newValues(newVal.query['endDates'], endDate, isAdd),
        }
      } else if (newLabel === 'status') {
        newVal.query = {
          ...newVal.query,
          statusAndBelows: newValues(
            newVal.query['statusAndBelows'],
            checkValue(queryValue),
            isAdd,
          ),
          statusAndAboves: newValues(
            newVal.query['statusAndAboves'],
            checkValue(queryValue),
            isAdd,
          ),
        }
      } else {
        newVal.query = {
          ...newVal.query,
          [newLabel]: newValues(
            newVal.query[newLabel],
            checkValue(queryValue),
            isAdd,
          ),
        }
      }
    }
  })

  return newVal
}

const getUpdatedMappedFilters = ({
  prev,
  updatedValues = [],
  date = {},
  isOnlyOne = false,
}) => {
  const newData = [...prev]
  return newData.reduce((acc, item) => {
    const value = updatedValues.find(value => value.label === item.data.label)
    if (value) {
      const { dateWeekNumber, dateYearNumber } = date
      if (item.data.label === 'Week' && dateWeekNumber && dateYearNumber) {
        const option = item.data.options.find(
          item =>
            item.value.weekNumber === dateWeekNumber &&
            item.value.year === dateYearNumber,
        )
        acc.push({
          ...item,
          data: { ...item.data, selectedValue: option.value },
        })
      } else {
        acc.push({
          ...item,
          data: { ...item.data, selectedValue: value.queryValue },
        })
      }
    } else {
      acc.push({
        ...item,
        data: {
          ...item.data,
          selectedValue: isOnlyOne ? item.data.selectedValue : 'All',
        },
      })
    }
    return acc
  }, [])
}

const onClickRow = (event, id) => {
  window.open(`/dashboard/mission/${id}`, '_blank')
}

const openBaPage = (cityId, baId) => {
  window.open(`/dashboard/cities/${cityId}/ambassadors/${baId}`, '_blank')
}

const getTitle = ({ numSelected }) => {
  if (!numSelected) {
    return 'Do you want assign create Spy Shift, please select Spys in column SPY'
  }

  if (numSelected) {
    return `You have selected ${numSelected} missions, assign Spy to create Spy Shift`
  }
}

const formatSchedule = schedule =>
  schedule
    ? {
        [getShortWeekDay(1)]: {
          MORNING: schedule.mondayMorning,
          LUNCH: schedule.mondayLunch,
          AFTERNOON: schedule.mondayAfternoon,
        },
        [getShortWeekDay(2)]: {
          MORNING: schedule.tuesdayMorning,
          LUNCH: schedule.tuesdayLunch,
          AFTERNOON: schedule.tuesdayAfternoon,
        },
        [getShortWeekDay(3)]: {
          MORNING: schedule.wednesdayMorning,
          LUNCH: schedule.wednesdayLunch,
          AFTERNOON: schedule.wednesdayAfternoon,
        },
        [getShortWeekDay(4)]: {
          MORNING: schedule.thursdayMorning,
          LUNCH: schedule.thursdayLunch,
          AFTERNOON: schedule.thursdayAfternoon,
        },
        [getShortWeekDay(5)]: {
          MORNING: schedule.fridayMorning,
          LUNCH: schedule.fridayLunch,
          AFTERNOON: schedule.fridayAfternoon,
        },
        [getShortWeekDay(6)]: {
          MORNING: schedule.saturdayMorning,
          LUNCH: schedule.saturdayLunch,
          AFTERNOON: schedule.saturdayAfternoon,
        },
        [getShortWeekDay(0)]: {
          MORNING: schedule.sundayMorning,
          LUNCH: schedule.sundayLunch,
          AFTERNOON: schedule.sundayAfternoon,
        },
      }
    : false

const filterSpies = ({ spies, missionId, rowsData }) => {
  const selectedMission = rowsData.find(row => row.params.id === missionId)
  return spies.filter(spy => {
    const schedule = formatSchedule(spy.schedule)
    if (!missionId) {
      return true
    }

    if (!schedule) {
      return false
    }

    const row = selectedMission.row

    return schedule[row.dayOfWeek][row.timeOfDay]
  })
}

const calculateTotals = ({
  data,
  setTotalRow,
  isCountryLevel,
  isCityManagerAssistant,
  isSpy,
}) => {
  const totals = data.reduce(
    (acc, item) => {
      acc.flyersDistributed += Number(item.flyersDistributed)
      acc.scans += Number(item.scans)

      if (item.metric1 && item.metric1 !== ' - ') {
        const splittedMetric = item.metric1.split(':')
        const value = splittedMetric[1]
        acc.metric1 = (acc.metric1 || 0) + Number(value)
      }

      if (item.metric2 && item.metric2 !== ' - ') {
        const splittedMetric = item.metric2.split(':')
        const value = splittedMetric[1]
        acc.metric2 = (acc.metric2 || 0) + Number(value)
      }

      return acc
    },
    {
      flyersDistributed: 0,
      scans: 0,
      metric1: 0,
      metric2: 0,
    },
  )

  let totalRow = [
    { label: 'totals', value: 'Totals' },
    { label: 'сampaign', value: '-' },
    { label: 'week', value: '-' },
    { label: 'date', value: '-' },
    { label: 'time', value: '-' },
    { label: 'timeOfday', value: '-' },
    { label: 'dayOfWeek', value: '-' },
    { label: 'canBeSpied', value: '-' },
    { label: 'spy', value: '-' },
    { label: 'location', value: '-' },
    { label: 'ba', value: '-' },
    { label: 'code', value: '-' },
    { label: 'tag', value: '-' },
    { label: 'flyersDistributed', value: totals.flyersDistributed },
    { label: 'scans', value: totals.scans },
    {
      label: 'mertic1',
      value: isCityManagerAssistant ? '-' : totals.metric1,
    },
    {
      label: 'metric2',
      value: isCityManagerAssistant ? '-' : totals.metric2,
    },
    { label: 'status', value: '-' },
    { label: 'rating', value: '-' },
  ]

  if (isSpy) {
    totalRow = totalRow.filter(
      header =>
        !['mertic1', 'metric2', 'rating', 'code'].includes(header.label),
    )
  }

  const countryTotalRow = [
    { label: 'totals', value: 'Totals' },
    { label: 'client', value: '-' },
    { label: 'сampaign', value: '-' },
    { label: 'week', value: '-' },
    { label: 'date', value: '-' },
    { label: 'time', value: '-' },
    { label: 'timeOfday', value: '-' },
    { label: 'dayOfWeek', value: '-' },
    { label: 'canBeSpied', value: '-' },
    { label: 'spy', value: '-' },
    { label: 'location', value: '-' },
    { label: 'ba', value: '-' },
    { label: 'code', value: '-' },
    { label: 'tag', value: '-' },
    { label: 'flyersDistributed', value: totals.flyersDistributed },
    { label: 'scans', value: totals.scans },
    {
      label: 'mertic1',
      value: isCityManagerAssistant ? '-' : totals.metric1,
    },
    {
      label: 'metric2',
      value: isCityManagerAssistant ? '-' : totals.metric2,
    },
    { label: 'status', value: '-' },
    { label: 'rating', value: '-' },
  ]

  if (isCountryLevel) return setTotalRow(countryTotalRow)

  setTotalRow(totalRow)
}

const getExportHeaders = shouldHideSensitiveData => {
  if (shouldHideSensitiveData) {
    return exportHeaders.filter(
      header => !['Metric 1', 'Metric 2'].includes(header),
    )
  }
  return exportHeaders
}

const exportCSV = (
  rows,
  cityNames,
  countryName = '',
  shouldHideSensitiveData,
) => {
  const formattedRows = formatRows(
    { country: { missions: { edges: rows } } },
    true,
  )
  const headers = getExportHeaders(shouldHideSensitiveData)
  let content = headers.join(',')
  formattedRows.nodes.forEach(r => {
    content +=
      '\n' +
      headers
        .map(h => {
          const val = r[camelCase(h)]
          return typeof val === 'string' ? escapeCsvString(val) : val
        })
        .join(',')
  })
  downloadStringAsFile(
    content,
    `${
      cityNames.length > 0
        ? cityNames
            .join(',')
            .toUpperCase()
            .replace(' ', '_')
        : countryName
    }.csv`,
    'text/csv',
  )
}

export {
  setUrl,
  calculateTimeOfDay,
  switchLabelToId,
  switchOrderBy,
  formatRows,
  checkValue,
  getUpdatedQueryAndLabels,
  getUpdatedMappedFilters,
  onClickRow,
  openBaPage,
  getTitle,
  filterSpies,
  calculateTotals,
  exportCSV,
}
